import type {ComponentType} from "react";
import type {AxiosError} from "axios";
import type {IApiResponse} from "data/services/http";
import {captureMessage} from "@sentry/react";
import {get, reduce} from "lodash";
import {Clipboard} from "ts-clipboard";

type Factory<T> = () => Promise<{
	default: ComponentType<T>;
}>;

export function getIsIOS() {
	if (!navigator) {
		return false;
	}
	return (
		["iPad Simulator", "iPhone Simulator", "iPod Simulator", "iPad", "iPhone", "iPod"].includes(
			navigator.platform
		) ||
		// iPad on iOS 13 detection
		(navigator.userAgent.includes("Mac") && "ontouchend" in document)
	);
}

export const wait = (durationMS: number) =>
	new Promise((resolve) => setTimeout(resolve, durationMS));

export function retryFailLoad<T>(fn: Factory<T>, retriesLeft = 5, interval = 1000): Factory<T> {
	return () =>
		new Promise((resolve, reject) => {
			fn()
				.then(resolve)
				.catch((error: unknown) => {
					setTimeout(() => {
						if (retriesLeft === 1) {
							reject(error);
							return;
						}

						retryFailLoad(fn, retriesLeft - 1, interval)().then(resolve, reject);
					}, interval);
				});
		});
}

export const extractErrorMessage = (e: AxiosError<IApiResponse> | unknown) => {
	const error = e as AxiosError<IApiResponse>;
	return error.response?.data?.errors?.[0]?.message || error.message;
};

export function getDashedValue<T>(value: T, replacer = "--"): string {
	if (value === null || value === undefined) {
		return replacer;
	}
	return String(value);
}

export function loadScript(
	src: string,
	callback: (err: Error | null, elem?: HTMLScriptElement) => void
) {
	const scriptInDom = document.querySelector(`script[src~="${src}"]`);
	if (scriptInDom) {
		callback(null, scriptInDom as HTMLScriptElement);
		return;
	}
	const script = document.createElement("script");
	script.src = src;
	script.async = true;
	script.onload = () => callback(null, script);
	script.onerror = () => callback(new Error(`Error script load fail ${src}`));

	document.head.append(script);
}

export const loadScriptPromise = function (src: string) {
	return new Promise((resolve, reject) => {
		loadScript(src, (err, script) => {
			if (err) reject(err);
			else resolve(script);
		});
	});
};

export const trackSentryErrors = (
	exception: unknown,
	context: Record<string, unknown>,
	contextName: string = "unknown error"
) => {
	const errorName = get(exception, "message", get(exception, "text", contextName));

	captureMessage(errorName, (scope) => {
		scope.setContext(
			contextName,
			reduce(
				context,
				(acc, value, key) => {
					if (context[key] !== undefined) {
						acc[key] = JSON.stringify(context[key]);
					}

					return acc;
				},
				{} as Record<string, unknown>
			)
		);

		scope.setTag("handler", contextName);
		scope.setTransactionName(contextName);

		return scope;
	});
};

export const copyToClipboard = async (text: string): Promise<boolean> => {
	if (navigator?.clipboard) {
		try {
			await navigator.clipboard.writeText(text);
			return true;
		} catch (e) {
			console.log(e);
		}
	}

	try {
		Clipboard.copy(text);
		return true;
	} catch (e) {
		console.log(e);
		return false;
	}
};
export * from "data/utils/countdown";
export * from "data/utils/socialShare";
